import { useEffect, useState } from "react";
import {
  Users,
  TrendingUp,
  UserCheck,
  UserMinus,
  Gift,
  CheckCircle,
  Paperclip,
  ChartPie,
} from "lucide-react";
import Skeleton from "@mui/material/Skeleton";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router";
import Style from "./style.module.css";
import WorkForceQuickView from "./popup";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { TableCard } from "../../../../component/table/table-layout";
import { CustomDropdown } from "../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../component/ui/dropdown/CustomDropdownMenuList";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import NoInsights from "../../../../component/ui/loader/no-chart-data";
import { KPI } from "../../../../component/ui/layout";
import { CustomHighChart } from "../../../../component/ui/chart/custom-chart";

const Card = ({ className = "", children }) => (
  <div
    className={`w-full border border-[#E1E4EA] shadow rounded-[14px] flex flex-col border-slate-200/60 1darkborder-slate-800 bg-white 1darkbg-slate-900 ${className}`}
  >
    {children}
  </div>
);
const CardHeader = ({ className = "", children }) => (
  <div
    className={`p-4 md:p-5 border-b border-slate-100 1darkborder-slate-800 ${className}`}
  >
    {children}
  </div>
);
const CardTitle = ({ className = "", children }) => (
  <h3
    className={`text-lg md:text-xl font-semibold tracking-tight ${className}`}
  >
    {children}
  </h3>
);
const CardContent = ({ className = "", children }) => (
  <div className={`p-4 md:p-5 w-full h-full ${className}`}>{children}</div>
);

// --- Main Component ---
export default function DealsTeamOverview() {
  // let data = "";
  const apiCall = useApiCall();
  const router = useNavigate();
  const [KPIData, setKPIData] = useState({});
  const [KPILoading, setKPILoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [revenueSelectedMonths, setRevenueSelectedMonths] = useState(6);
  const [forcastDays, setForcastDays] = useState(7);

  const getKPIData = async () => {
    setKPILoading(true);
    try {
      const resp = await apiCall({
        method: "get",
        url: `deals/deal-overview-kpi?scope=team`,
      });
      setKPIData(resp?.data);
      setKPILoading(false);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    getKPIData();
    getTrendData();
    getRevenueData();
    getForcastData();
  }, []);

  // LINE CHART
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const getTrendData = async () => {
    setTrendLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: "deals/deal-overview-trend?scope=team",
      });
      setTrendData(res.data);
    } catch (error) {
      console.warn("Error fetching trend data:", error);
    } finally {
      setTrendLoading(true);
    }
  };

  const lineOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 300,
    },

    title: { text: "" },

    xAxis: {
      categories: trendData?.slice(-selectedMonths)?.map((d) => d.month),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },

    yAxis: {
      title: { text: "Deal Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },

    legend: {
      align: "right",
      verticalAlign: "top",
    },

    series: [
      {
        name: "Total Deals",
        data: trendData?.slice(-selectedMonths)?.map((d) => d?.total_deal),
        color: "#2563EB",
      },
      {
        name: "Won Deals",
        data: trendData?.slice(-selectedMonths)?.map((d) => d.won_deal),
        color: "#1FC16B",
      },
      {
        name: "Lost Deals",
        data: trendData?.slice(-selectedMonths)?.map((d) => d.lost_deal),
        color: "#F43F5E",
      },
    ],

    credits: { enabled: false },
  };

  // PIE CHART - Deals By Stages
  const [brandData, setBrandData] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState("this_month");
  const [allBrandsCount, setAllBrandsCount] = useState(false);

  const getLeadsByBrandData = async () => {
    setBrandLoading(false);
    const apiUrl = `deals/deal-overview-funnel?scope=team&time=${brandFilter}`;
    try {
      const res = await apiCall({ method: "get", url: apiUrl });
      setBrandData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.count) === 0);
      setAllBrandsCount(isAllZero);
    } catch (error) {
      console.warn("Error fetching brand data:", error);
    } finally {
      setBrandLoading(true);
    }
  };

  useEffect(() => {
    getLeadsByBrandData();
  }, [brandFilter]);

  const brandOptions = {
    chart: {
      type: "pie",
    },
    title: "none",
    credits: { enabled: false },
    tooltip: {
      pointFormat: "{series.stage}<b>{point.count}</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>:({point.count})",
        },
      },
    },
    series: [
      {
        name: "Deal Stages",
        colorByPoint: true,
        data: brandData?.map((brand) => ({
          count: brand?.count,
          name: brand?.stage,
          y: parseFloat(brand.count),
        })),
      },
    ],
    legend: { enabled: false }, // single series, legend not needed
  };

  // DONUT CHART - Deals By Products
  const [sourceData, setSourceData] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("this_month");
  const [allCount, setAllCount] = useState(false);

  const getDealsFunneralData = async () => {
    setSourceLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: `deals/deal-overview-product?scope=team&time=${sourceFilter}`,
      });
      setSourceData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.deals) === 0);
      setAllCount(isAllZero);
    } catch (error) {
    } finally {
      setSourceLoading(true);
    }
  };

  useEffect(() => {
    getDealsFunneralData();
  }, [sourceFilter]);

  const sourceOptions = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 320,
      spacing: [20, 0, 20, 0],
    },
    title: null,
    credits: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> Deals<br/>({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "65%",
        borderWidth: 2,
        borderColor: "#fff",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: 15,
          style: { color: "#374151", fontSize: "12px" },
          format: "{point.name}: {point.percentage:.1f}%",
          // filter: { property: "percentage", operator: ">", value: 2 },
          filter: null,
        },
        showInLegend: true,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { color: "#6B7280", fontSize: "12px" },
    },
    series: [
      {
        name: "Leads",
        colorByPoint: true, // disable auto colors
        data: sourceData?.map((el) => ({
          name: el?.product,
          y: el?.deals,
          // color: (() => {
          //   switch (el?.product) {
          //     case "Web":
          //       return "#3b5998";
          //     case "Logo":
          //       return "#f4b400";
          //     case "Animation":
          //       return "#34a853";
          //     case "Printing":
          //       return "#6c757d";
          //     case "Animated Logo":
          //       return "#ff5722";
          //     case "SEO":
          //       return "#9c27b0";
          //     case "Branding":
          //       return "#00bcd4";
          //     case "Social Media":
          //       return "#e91e63";
          //     case "Email Campaign":
          //       return "#795548";
          //     case "Referral":
          //       return "#ffc107";
          //     case "Website":
          //       return "#607d8b";
          //     case "Unknown":
          //       return "#888888";
          //     default:
          //       return "#888888";
          //   }
          // })(),
        })),
      },
    ],
    events: {
      render() {
        const chart = this;
        const total = chart.series[0].data.reduce((acc, p) => acc + p.y, 0);
        const labelX = chart.plotWidth / 2 + chart.plotLeft - 25;
        const labelY = chart.plotHeight / 2 + chart.plotTop - 25;

        if (!chart.customLabel) {
          chart.customLabel = chart.renderer
            .label(`Total<br><b>${total}</b>`, labelX, labelY)
            .css({ textAlign: "center", color: "#111827", fontSize: "13px" })
            .add();
        } else {
          chart.customLabel.attr({ text: `Total<br><b>${total}</b>` });
        }
      },
    },
  };

  // Revenue CHART
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const getRevenueData = async () => {
    setRevenueLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: "deals/deal-overview-revenue?scope=team",
      });
      setRevenueData(res.data.months);
    } catch (error) {
      console.warn("Error fetching revenue data:", error);
    } finally {
      setRevenueLoading(true);
    }
  };

  const revenueOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 300,
    },

    title: { text: "" },

    xAxis: {
      categories: revenueData
        ?.slice(-revenueSelectedMonths)
        ?.map((d) => d.month),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },

    yAxis: {
      title: { text: "Revenue Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },

    legend: {
      align: "right",
      verticalAlign: "top",
    },

    series: [
      {
        name: "Total Revenue",
        data: revenueData?.slice(-revenueSelectedMonths)?.map((d) => d?.amount),
        color: "#2563EB",
      },
    ],

    credits: { enabled: false },
  };

  // Forcast Chart
  const [forcastData, setForcastData] = useState([]);
  const [forcastLoading, setForcastLoading] = useState(false);

  const getForcastData = async () => {
    setForcastLoading(true);
    try {
      const res = await apiCall({
        method: "get",
        url: "deals/deal-overview-forecast?scope=team",
      });
      setForcastData(res?.data?.forecast);
    } catch (error) {
      console.warn("Error fetching forcast data:", error);
    } finally {
      setForcastLoading(false);
    }
  };

  function generateDateRange(start, end) {
    if (!start || !end) return []; // ✅ early exit if start/end missing

    const dates = [];
    let current = new Date(start);
    const last = new Date(end);

    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]); // YYYY-MM-DD
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  const filteredForecastData = forcastData?.slice(0, forcastDays) || [];

  let chartData;

  if (!forcastLoading && filteredForecastData.length > 0) {
    const startDate = new Date(filteredForecastData[0]?.date);
    const endDate = new Date(
      filteredForecastData[filteredForecastData?.length - 1].date
    );

    const fullDates = generateDateRange(startDate, endDate);

    chartData = fullDates?.map((date) => {
      const found = forcastData?.find((d) => d?.date == date);
      return { date, amount: found?.amount || 0 };
    });
  }

  const forcastOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 300,
    },

    title: { text: "" },

    xAxis: {
      // categories: chartData?.map((d) => d.date),
      categories: chartData?.map((d) =>
        new Date(d?.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      ),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },

    yAxis: {
      title: { text: "Forecast Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },

    legend: {
      align: "right",
      verticalAlign: "top",
    },

    series: [
      {
        name: "Total Forecast",
        data: chartData?.map((d) => d?.amount),
        color: "#2563EB",
      },
    ],

    credits: { enabled: false },
  };

  // DONUT CHART - Leads by Source
  const [packageData, setPackageData] = useState([]);
  const [packageDataLoading, setPackageDataLoading] = useState(false);
  const [packageDataFilter, setpackageDataFilter] = useState("this_month");
  const [packageDataAllCount, setPackageDataAllCount] = useState(false);

  const getPackageData = async () => {
    setPackageDataLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: `deals/deal-overview-package?scope=team&time=${sourceFilter}`,
      });
      setPackageData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.deals) === 0);
      setPackageDataAllCount(isAllZero);
    } catch (error) {
    } finally {
      setPackageDataLoading(true);
    }
  };

  useEffect(() => {
    getPackageData();
  }, [packageDataFilter]);

  const packageDataOptions = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 320,
      spacing: [20, 0, 20, 0],
    },
    title: null,
    credits: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> Deals<br/>({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "65%",
        borderWidth: 2,
        borderColor: "#fff",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: 15,
          style: { color: "#374151", fontSize: "12px" },
          format: "{point.name}: {point.percentage:.1f}%",
          // filter: { property: "percentage", operator: ">", value: 2 },
          filter: null,
        },
        showInLegend: true,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { color: "#6B7280", fontSize: "12px" },
    },
    series: [
      {
        name: "Deals",
        colorByPoint: true, // disable auto colors
        data: packageData?.map((el) => ({
          name: el?.package,
          y: el?.deals,
        })),
      },
    ],
    events: {
      render() {
        const chart = this;
        const total = chart.series[0].data.reduce((acc, p) => acc + p.y, 0);
        const labelX = chart.plotWidth / 2 + chart.plotLeft - 25;
        const labelY = chart.plotHeight / 2 + chart.plotTop - 25;

        if (!chart.customLabel) {
          chart.customLabel = chart.renderer
            .label(`Total<br><b>${total}</b>`, labelX, labelY)
            .css({ textAlign: "center", color: "#111827", fontSize: "13px" })
            .add();
        } else {
          chart.customLabel.attr({ text: `Total<br><b>${total}</b>` });
        }
      },
    },
  };

  const [showWorkForce, setShowWorkForce] = useState(false);
  const [popupData, setPopupData] = useState({});

  return (
    <>
      <div className="pt-[20px] overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold">Team Metrics</h1>
          </div>

          <TableCard className={"h-full overflow-hidden"}>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {!KPILoading ? (
                 <>
                  <KPI
                    icon={Users}
                    label="Total Deals"
                    value={KPIData?.total_deals}
                    sublabel={"Total"}
                    extraClass={"theme"}
                  />
                  <KPI
                    icon={UserCheck}
                    label="New Deals"
                    value={KPIData?.total_deals_this_month}
                    sublabel={`This Month`}
                  />
                  <KPI
                    icon={UserMinus}
                    label="Won Deals"
                    value={KPIData?.won_deals}
                    sublabel={`This Month`}
                    extraClass={"green"}
                  />
                  <KPI
                    icon={Gift}
                    label="Lost Deals"
                    value={KPIData?.lost_deals}
                    sublabel={`This Month`}
                    extraClass={"red"}
                  />

                  <KPI
                    icon={CheckCircle}
                    label="Open Deals"
                    value={KPIData?.open_deals}
                    sublabel={`This Month`}
                    // extraClass={"green"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Conversion Rate"
                    value={KPIData?.conversion_rate_this_month}
                    sublabel={`This Month`}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Total Revenue"
                    value={KPIData?.total_revenue_this_month}
                    sublabel={`This Month`}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Avg Deal Value"
                    value={KPIData?.avg_deal_value}
                    sublabel={`This Month`}
                    // extraClass={"yellow"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Avg Time To Won Deal"
                    value={KPIData?.avg_time_to_won_deal}
                    sublabel={`This Month`}
                    extraClass={"green"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Total Product Sold"
                    value={KPIData?.total_products_sold}
                    sublabel={`This Month`}
                    // extraClass={"green"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Today New Deals"
                    value={KPIData?.today_new_deals}
                    sublabel={`Today`}
                    // extraClass={"green"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Today Won Deals"
                    value={KPIData?.today_won_deals}
                    sublabel={`Today`}
                    extraClass={"green"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Today Lost Deals"
                    value={KPIData?.today_lost_deals}
                    sublabel={`Today`}
                    extraClass={"red"}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Today Open Deals"
                    value={KPIData?.today_open_deals}
                    sublabel={`Today`}
                    // extraClass={"yellow"}
                  />
                </>
              ) : (
                Array.from({ length: 10 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white shadow rounded-xl p-4 animate-pulse"
                  >
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1 mt-2 py-[12px]">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
              {/* Line Chart */}
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> By Deals
                  </CardTitle>

                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(6)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 6 ? Style.active : ""
                        }`}
                      ></div>
                      <p>6 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(9)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 9 ? Style.active : ""
                        }`}
                      ></div>
                      <p>9 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(12)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 12 ? Style.active : ""
                        }`}
                      ></div>
                      <p>12 Months</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* {trendLoading ? (
                    trendData?.length > 0 ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={lineOptions}
                      />
                    ) : (
                      <>
                        <NoInsights message="Data not Found" />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={lineOptions}  isLoad={trendLoading} titleNotFound="No Data Found"/>
                </CardContent>
              </Card>
              {/* Donut Chart */}
              <Card className="col-span-2 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ChartPie className="h-5 w-5" />
                    By Stage
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {brandFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {brandFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}

                      {brandFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {/* {brandLoading ? (
                    brandData?.length > 0 && !allBrandsCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={brandOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={brandOptions}  isLoad={brandLoading}/>
                </CardContent>
              </Card>
              {/* Product Chart */}
              <Card className="col-span-2 min-h-[400px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="h-5 w-5" /> Deals By Products
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {sourceFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {sourceFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {/* {sourceLoading ? (
                    sourceData?.length > 0 && !allCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={sourceOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={sourceOptions}  isLoad={sourceLoading}/>
                </CardContent>
              </Card>
              {/* Revenue Chart */}
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> By Deals Revenue
                  </CardTitle>

                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setRevenueSelectedMonths(6)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          revenueSelectedMonths == 6 ? Style.active : ""
                        }`}
                      ></div>
                      <p>6 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setRevenueSelectedMonths(9)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          revenueSelectedMonths == 9 ? Style.active : ""
                        }`}
                      ></div>
                      <p>9 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setRevenueSelectedMonths(12)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          revenueSelectedMonths == 12 ? Style.active : ""
                        }`}
                      ></div>
                      <p>12 Months</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* {revenueLoading ? (
                    revenueData?.length > 0 ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={revenueOptions}
                      />
                    ) : (
                      <>
                        <NoInsights message="Data not Found" />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={revenueOptions}  isLoad={revenueLoading} titleNotFound="No Data Found"/>
                </CardContent>
              </Card>
              {/* Forecast Chart */}
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> Forecast
                  </CardTitle>

                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setForcastDays(7)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          forcastDays == 7 ? Style.active : ""
                        }`}
                      ></div>
                      <p>7 days</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setForcastDays(14)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          forcastDays == 14 ? Style.active : ""
                        }`}
                      ></div>
                      <p>14 days</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setForcastDays(30)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          forcastDays == 30 ? Style.active : ""
                        }`}
                      ></div>
                      <p>30 days</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* {!forcastLoading ? (
                    forcastData?.length > 0 ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={forcastOptions}
                      />
                    ) : (
                      <>
                        <NoInsights message="Data not Found" />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={forcastOptions}  isLoad={!forcastLoading} titleNotFound="No Data Found"/>
                </CardContent>
              </Card>
              {/* Pacakge Chart */}
              <Card className="col-span-2 min-h-[400px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="h-5 w-5" /> Deals By Package
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {packageDataFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {packageDataFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setpackageDataFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}
                      {packageDataFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setpackageDataFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}

                      {packageDataFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setpackageDataFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {packageDataFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setpackageDataFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}

                      {packageDataFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setpackageDataFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {/* {packageDataLoading ? (
                    packageData?.length > 0 && !packageDataAllCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={packageDataOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart options={packageDataOptions}  isLoad={packageDataLoading}/>
                </CardContent>
              </Card>
            </div>
          </TableCard>
        </div>
      </div>

      {showWorkForce && (
        <WorkForceQuickView
          onClose={() => setShowWorkForce(false)}
          apiKey={popupData.apiKey}
          apiUrl={popupData.apiUrl}
          pagUrl={popupData.pagUrl}
          title={popupData.title}
          subTitle={popupData.subTitle}
          icon={popupData.icon}
          positive={popupData?.positive}
          stateLable={popupData?.stateLable}
          positiveIcon={popupData?.positiveIcon}
        />
      )}
    </>
  );
}
